// Modular horizontal scroll container for small screens (720p and below)
import React, { ReactNode, useRef, useEffect, useState } from 'react';
import { cn } from '@utils/classNames';

interface HorizontalScrollContainerProps {
  children: ReactNode;
  className?: string;
  /** Enable horizontal scrolling on screens 1280px and below (720p) */
  enableOnSmallScreens?: boolean;
  /** Custom breakpoint in pixels (default: 1280px for 720p) */
  breakpoint?: number;
}

/**
 * Modular horizontal scroll container component
 * Enables horizontal scrolling only when content overflows
 * and only on small screens (720p/1280px and below)
 */
export const HorizontalScrollContainer: React.FC<HorizontalScrollContainerProps> = ({
  children,
  className,
  enableOnSmallScreens = true,
  breakpoint = 1280,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current || !contentRef.current) return;

      const isSmall = window.innerWidth <= breakpoint;
      setIsSmallScreen(isSmall);

      if (!isSmall) {
        setHasOverflow(false);
        return;
      }

      // Check if content overflows
      const containerWidth = containerRef.current.clientWidth;
      const contentWidth = contentRef.current.scrollWidth;
      setHasOverflow(contentWidth > containerWidth);
    };

    // Initial check
    checkOverflow();

    // Check on resize
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [breakpoint]);

  if (!enableOnSmallScreens) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full min-w-0',
        isSmallScreen && hasOverflow && 'overflow-x-auto overflow-y-visible',
        !isSmallScreen && 'overflow-x-hidden',
        !hasOverflow && 'overflow-x-hidden',
        'horizontal-scroll-container',
        className
      )}
      style={{
        scrollBehavior: 'smooth',
      }}
    >
      <div ref={contentRef} className="w-full min-w-0">
        {children}
      </div>
    </div>
  );
};

